"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { generateBreadcrumbSchema } from "@/utils/schemaGenerators";

export interface BreadcrumbCrumb {
  label: string;
  href?: string;
}

export interface BreadcrumbWidgetProps {
  crumbs?: BreadcrumbCrumb[];
}

export default function BreadcrumbWidget({ crumbs = [] }: BreadcrumbWidgetProps) {
  const pathname = usePathname();
  const [isClientMounted, setIsClientMounted] = useState(false);

  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  const normalizedCrumbs = useMemo(() => {
    return (Array.isArray(crumbs) ? crumbs : [])
      .filter((crumb) => crumb?.label)
      .map((crumb) => ({
        label: String(crumb.label),
        href: String(crumb.href || ""),
      }));
  }, [crumbs]);

  const desktopCrumbs = normalizedCrumbs;
  const mobileCrumbs = normalizedCrumbs.slice(-2);

  const schema = useMemo(() => {
    if (normalizedCrumbs.length === 0) {
      return null;
    }

    const schemaCrumbs = normalizedCrumbs.map((crumb) => ({
      name: crumb.label,
      url: crumb.href || pathname || "/",
    }));

    return generateBreadcrumbSchema(schemaCrumbs);
  }, [normalizedCrumbs, pathname]);

  if (normalizedCrumbs.length === 0) {
    return null;
  }

  return (
    <section className="bg-white border-b border-gray-100">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 py-3 md:py-4">
        <nav aria-label="Breadcrumb" className="text-sm text-[#666666]">
          <ol className="hidden md:flex items-center gap-2 flex-wrap">
            {desktopCrumbs.map((crumb, index) => {
              const isLast = index === desktopCrumbs.length - 1;

              return (
                <li key={`${crumb.label}-${index}`} className="inline-flex items-center gap-2">
                  {!isLast && crumb.href ? (
                    <Link prefetch={true} href={crumb.href} className="hover:text-[#1B61FF] transition-colors">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-[#1A1A1A] font-medium">{crumb.label}</span>
                  )}

                  {!isLast ? <span aria-hidden="true">›</span> : null}
                </li>
              );
            })}
          </ol>

          <ol className="flex md:hidden items-center gap-2">
            {mobileCrumbs.map((crumb, index) => {
              const isLast = index === mobileCrumbs.length - 1;

              return (
                <li key={`mobile-${crumb.label}-${index}`} className="inline-flex items-center gap-2 min-w-0">
                  {!isLast && crumb.href ? (
                    <Link prefetch={true} href={crumb.href} className="hover:text-[#1B61FF] transition-colors truncate max-w-[42vw]">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-[#1A1A1A] font-medium truncate max-w-[52vw]">{crumb.label}</span>
                  )}

                  {!isLast ? <span aria-hidden="true">›</span> : null}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {isClientMounted && schema ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ) : null}
    </section>
  );
}

